import React, { useEffect, useState } from "react";

const AddLocation = () => {
    const [data, setData] = useState(null);
    const [newDistrict, setNewDistrict] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [editingDistrict, setEditingDistrict] = useState(null);
    const [editValue, setEditValue] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");

    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

    // Fetch data safely
    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                setError("");
                const res = await fetch(`${apiBaseUrl}/data`);
                if (!res.ok) throw new Error("Failed to load locations");
                const result = await res.json();
                setData(result);
            } catch (err) {
                console.error("Fetch error:", err);
                setError("Failed to load locations. Please refresh the page.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [apiBaseUrl]);

    const refreshData = async () => {
        try {
            const res = await fetch(`${apiBaseUrl}/data`);
            if (!res.ok) throw new Error("Refresh failed");
            const result = await res.json();
            setData(result);
        } catch (err) {
            alert("Could not refresh. Please reload the page.");
        }
    };

    // Helper: Get current districts array safely
    const getDistricts = () => {
        try {
            return Array.isArray(data?.states?.[0]?.districts)
                ? data.states[0].districts
                : [];
        } catch {
            return [];
        }
    };

    const handleAddDistrict = async () => {
        const trimmed = newDistrict.trim();
        if (!trimmed) return alert("Please enter a location name");

        const districts = getDistricts();
        const lowerTrimmed = trimmed.toLowerCase();

        // Check for duplicate (case-insensitive)
        const isDuplicate = districts.some((d) => d.toLowerCase() === lowerTrimmed);
        if (isDuplicate) {
            return alert(`"${trimmed}" already exists!`);
        }

        try {
            const res = await fetch(`${apiBaseUrl}/addDistrict`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ district: trimmed }),
            });

            const result = await res.json();
            if (!res.ok) throw new Error(result.error || "Failed to add location");

            setData(result.data);
            setNewDistrict("");
            alert("Location added successfully!");
        } catch (err) {
            alert(err.message || "Failed to add location");
        }
    };

    const handleDeleteDistrict = async (district) => {
        if (!window.confirm(`Delete "${district}" permanently?`)) return;

        try {
            const res = await fetch(`${apiBaseUrl}/deleteDistrict`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ district }),
            });

            const result = await res.json();
            if (!res.ok) throw new Error(result.error || "Failed to delete");

            setData(result.data);
            alert("Location deleted successfully!");
        } catch (err) {
            alert(err.message || "Failed to delete location");
        }
    };

    const handleSaveEdit = async () => {
        const trimmed = editValue.trim();
        if (!trimmed) return alert("Location name cannot be empty");
        if (trimmed === editingDistrict) return cancelEdit();

        const districts = getDistricts();
        const lowerTrimmed = trimmed.toLowerCase();

        // Check duplicate during edit (excluding the current one being edited)
        const exists = districts.some(
            (d) => d.toLowerCase() === lowerTrimmed && d !== editingDistrict
        );
        if (exists) return alert("This location already exists!");

        setIsSaving(true);
        try {
            const res = await fetch(`${apiBaseUrl}/updateDistrict`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    oldDistrict: editingDistrict,
                    newDistrict: trimmed,
                }),
            });

            const result = await res.json();
            if (!res.ok) throw new Error(result.error || "Update failed");

            setData(result.data);
            cancelEdit();
            alert("Location updated successfully!");
        } catch (err) {
            alert(err.message || "Update failed");
        } finally {
            setIsSaving(false);
        }
    };

    const cancelEdit = () => {
        setEditingDistrict(null);
        setEditValue("");
        setIsSaving(false);
    };

    const districts = getDistricts();
    const filtered = districts.filter((d) =>
        d.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (isLoading) return <p className="text-center py-20 text-gray-600">Loading locations...</p>;
    if (error) return <p className="text-center py-20 text-red-600">{error}</p>;

    return (
        <div className="min-h-screen bg-gray-100 py-8 px-4">
            <div className="max-w-2xl mx-auto bg-white shadow-lg rounded-xl p-6 md:p-8">
                <h1 className="text-3xl font-bold text-center text-gray-800 mb-8">Manage Locations</h1>

                {/* Add New */}
                <div className="flex flex-col sm:flex-row gap-3 mb-8">
                    <input
                        type="text"
                        value={newDistrict}
                        onChange={(e) => setNewDistrict(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleAddDistrict()}
                        placeholder="Add new location"
                        className="flex-1 px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                        onClick={handleAddDistrict}
                        className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition"
                    >
                        Add Location
                    </button>
                </div>

                {/* Search */}
                <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search locations..."
                    className="w-full mb-6 px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />

                {/* List */}
                <ul className="space-y-3">
                    {filtered.length === 0 ? (
                        <p className="text-center text-gray-500 py-8">No locations found</p>
                    ) : (
                        filtered.map((district) => (
                            <li
                                key={district}
                                className="flex items-center justify-between bg-gray-50 p-4 rounded-lg border hover:shadow transition"
                            >
                                {editingDistrict === district ? (
                                    <div className="flex items-center gap-2 flex-1">
                                        <input
                                            type="text"
                                            value={editValue}
                                            onChange={(e) => setEditValue(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") handleSaveEdit();
                                                if (e.key === "Escape") cancelEdit();
                                            }}
                                            className="flex-1 px-3 py-2 border rounded focus:outline-none focus:ring"
                                            autoFocus
                                        />
                                        <button
                                            onClick={handleSaveEdit}
                                            disabled={isSaving}
                                            className={`px-4 py-2 text-sm text-white rounded transition ${
                                                isSaving ? "bg-gray-400" : "bg-green-500 hover:bg-green-600"
                                            }`}
                                        >
                                            {isSaving ? "Saving..." : "Save"}
                                        </button>
                                        <button
                                            onClick={cancelEdit}
                                            className="px-4 py-2 text-sm text-white bg-gray-500 rounded hover:bg-gray-600"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <span className="font-medium">{district}</span>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => {
                                                    setEditingDistrict(district);
                                                    setEditValue(district);
                                                }}
                                                className="px-4 py-2 text-sm text-white bg-blue-500 rounded hover:bg-blue-600"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleDeleteDistrict(district)}
                                                className="px-4 py-2 text-sm text-white bg-red-500 rounded hover:bg-red-600"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </>
                                )}
                            </li>
                        ))
                    )}
                </ul>

                <div className="text-center mt-8">
                    <button onClick={refreshData} className="text-sm text-gray-600 hover:text-gray-800 underline">
                        Refresh List
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddLocation;